import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ContentPanel, ContentBox } from '../../../../Maps';
import { Box, Button, Typography, Divider, Stack, Chip } from '@esgian/esgianui';
import { PageLinkIcon, VesselIcon } from '@components';
import { useFleets, useSegment } from '@hooks';
import FleetVesselsTable from './FleetVesselsTable';
import FleetTopPortsTable from './FleetTopPortsTable';
import FleetActivityProfile from './FleetActivityProfile';
import FleetAverageSpeed from './FleetAverageSpeed';
import { ACTIVITY_MAP_SEARCH_PARAM_KEYS, SIDE_PANEL_TYPES } from '../constants';
import { useSearchParams } from 'react-router-dom';
import { useMapState } from '../hooks/useMapState';
import { useNavigate } from 'react-router-dom';
import { DATE_TIME_FORMAT } from '@constants';
import moment from 'moment';
import { VoyageType } from '@components/Sections/VoyageAnalytics/VoyageUtils';
import { setTempOngoingVoyageGroup } from '@pages/VoyageAnalytics/hooks/useVoyageGroups';

const FleetDetailsPanel = ({ fleetIds, handleClose, filteredVessels }) => {
  const { savedFleets, isLoadingFleets, removeVesselFromFleet } = useFleets();
  const [, setSearchParams] = useSearchParams();
  const { handleVesselSelect } = useMapState();
  const { uriExt, segment } = useSegment();
  const navigate = useNavigate();

  const [previousDays, setPreviousDays] = useState(30);

  const selectedFleets = useMemo(() => {
    if (!savedFleets?.length) return [];
    return savedFleets.filter((fleet) => fleetIds.includes(fleet.id));
  }, [savedFleets, fleetIds]);

  const allVessels = useMemo(() => {
    if (!selectedFleets?.length) return [];

    // Collect all vessels from all fleets, avoiding duplicates by vesselImo
    const uniqueVessels = new Map();

    selectedFleets.forEach((fleet) => {
      fleet.vessels?.forEach((vessel) => {
        const vesselDetailsInfo = filteredVessels.find((v) => v.imo === vessel.vesselImo);
        // If the vessel is not already in our map, add it
        // If it is, we'll keep the first occurrence (from the first fleet)
        if (!uniqueVessels.has(vessel.vesselImo)) {
          uniqueVessels.set(vessel.vesselImo, {
            ...vessel,
            ...vesselDetailsInfo,
            fleetId: fleet.id,
            fleetName: fleet.name,
            fleetColor: fleet.color
          });
        }
      });
    });

    return Array.from(uniqueVessels.values());
  }, [selectedFleets]);

  const vesselImos = useMemo(
    () => allVessels.map((v) => v.vesselImo || v.imo).filter(Boolean),
    [allVessels]
  );

  const handleRemoveVessel = async (vesselImo) => {
    const vessel = allVessels.find((v) => v.vesselImo === vesselImo);
    if (!vessel) return;

    try {
      await removeVesselFromFleet({
        fleetId: vessel.fleetId,
        vesselImo
      });
    } catch (error) {
      console.error('Error removing vessel:', error);
    }
  };

  const handleViewVesselPosition = (vesselImo) => {
    // Open vessel details and close the fleet panel
    setSearchParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.set(ACTIVITY_MAP_SEARCH_PARAM_KEYS.SIDE_PANEL_TYPE, SIDE_PANEL_TYPES.VESSEL_DETAILS);
      updated.set(ACTIVITY_MAP_SEARCH_PARAM_KEYS.SIDE_PANEL_ITEM_ID, vesselImo.toString());
      return updated;
    });

    // Trigger map to focus on vessel
    handleVesselSelect(vesselImo);
  };

  const handleViewFleetDetails = () => {
    if (!allVessels?.length) return;

    // Create a temp voyage group using all vessels from all selected fleets
    const fleetNames = selectedFleets.map((f) => f.name).join(', ');
    const tempVoyageGroup = {
      id: `ongoingVoyageGroup~${previousDays}~temp`,
      name:
        selectedFleets.length > 1
          ? `Multiple Fleets (${selectedFleets.length}) - Voyages`
          : `${fleetNames} - Fleet Voyages`,
      type: VoyageType.ONGOING,
      voyages: allVessels.map((vessel) => ({
        imo: vessel.vesselImo,
        vesselName: vessel.vesselName,
        departureDate: moment()
          .startOf('day')
          .subtract(previousDays + 1, 'days')
          .format(DATE_TIME_FORMAT),
        note: vessel.note || ''
      }))
    };

    // Save to localStorage
    setTempOngoingVoyageGroup(tempVoyageGroup);

    // Navigate to voyage group page
    navigate(`${uriExt}/voyage-group/${tempVoyageGroup.id}`);
  };

  if (!selectedFleets.length && !isLoadingFleets) {
    return null;
  }

  const title =
    selectedFleets.length === 1
      ? selectedFleets[0]?.name
      : `Multiple Fleets (${selectedFleets.length})`;

  return (
    <ContentPanel
      title={title || 'Loading...'}
      footerActions={
        <Box textAlign="center">
          <Button
            startIcon={<PageLinkIcon />}
            sx={{ textTransform: 'none', color: 'black' }}
            onClick={handleViewFleetDetails}
            disabled={!allVessels?.length}>
            View Voyage Group
          </Button>
        </Box>
      }
      onClose={handleClose}>
      <Box display="flex" flexDirection="column" gap={2}>
        <ContentBox isLoading={isLoadingFleets} skeletonHeight="200px">
          <Box>
            <Typography variant="h6" gutterBottom>
              Fleet Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {selectedFleets.map((fleet) => (
                  <Chip
                    key={fleet.id}
                    label={fleet.name}
                    size="small"
                    icon={<VesselIcon color={fleet.color} size={12} />}
                    sx={{ mb: 1, px: 2 }}
                  />
                ))}
              </Box>
              <Typography variant="body2">Total Vessels: {allVessels?.length || 0}</Typography>
            </Stack>
          </Box>
        </ContentBox>

        <ContentBox skeletonHeight="300px">
          <FleetTopPortsTable
            vesselImos={vesselImos}
            segmentId={segment.id}
            setPreviousDays={setPreviousDays}
          />
        </ContentBox>

        <ContentBox isLoading={isLoadingFleets} skeletonHeight="300px">
          <FleetVesselsTable
            vessels={allVessels}
            height="400px"
            fleetColor={selectedFleets[0]?.color}
            onRemoveVessel={handleRemoveVessel}
            onViewVesselPosition={handleViewVesselPosition}
            fileName={`fleet-vessels-${
              selectedFleets.length > 1 ? 'multiple' : selectedFleets[0]?.name
            }`}
            showFleetInfo={selectedFleets.length > 1}
          />
        </ContentBox>

        <ContentBox isLoading={isLoadingFleets} skeletonHeight="300px">
          <FleetAverageSpeed
            vesselImos={vesselImos}
            fleetName={selectedFleets.length === 1 ? selectedFleets[0]?.name : 'Multiple-Fleets'}
          />
        </ContentBox>

        <ContentBox isLoading={isLoadingFleets} skeletonHeight="300px">
          <FleetActivityProfile
            vesselImos={vesselImos}
            fleetName={selectedFleets.length === 1 ? selectedFleets[0]?.name : 'Multiple-Fleets'}
          />
        </ContentBox>
      </Box>
    </ContentPanel>
  );
};

FleetDetailsPanel.propTypes = {
  fleetIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleClose: PropTypes.func,
  filteredVessels: PropTypes.array
};

export default FleetDetailsPanel;
